"use client"

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit2, Plus, Trash2, Save, X } from 'lucide-react';
import { useEmployeeProfileStore } from '../../../stores/employeeProfileStore';
import { educationService } from '../../../services/educationService';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';
import { Button } from '@t-one-internal/design-system';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@t-one-internal/design-system/lib/components/ui/table';
import { Input } from '@t-one-internal/design-system/lib/components/ui/input';

type EducationFieldKey = 'university' | 'education' | 'degree' | 'year';

interface EducationProfileSectionProps {
  employee: EmployeeDetails;
  onUpdate?: (updates: Partial<EmployeeDetails>) => void;
  readOnly?: boolean;
  isEditing?: boolean;
  disableEdit?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

const EducationProfileSection: React.FC<EducationProfileSectionProps> = ({
  employee: _employee,
  onUpdate: _onUpdate,
  readOnly = false,
  isEditing: externalIsEditing,
  disableEdit = false,
  onEditingChange,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = setInternalIsEditing;
  const [isSaving, setIsSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<{ university: string, education: string, degree: string, year: string }>>>({});
  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  const { selectedEmployee, addEducation, removeEducation, updateEducation } = useEmployeeProfileStore();

  const educationEntries = selectedEmployee?.educations ?? [];
  const visibleEducationEntries = educationEntries.filter(entry => !pendingDeletions.includes(entry.id));

  const handleAddEducation = async () => {
    await addEducation({
      university: null,
      education: null,
      degree: null,
      year: null,
    });
  };

  const handleRemoveEducation = (id: string) => {
    setPendingDeletions(prev => [...prev, id]);
  };

  const handleUpdateEducation = (id: string, field: EducationFieldKey, value: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleSave = useCallback(async (skipEditingToggle = false) => {
    if (isSaving) return;
    setIsSaving(true);
    const userIdentifier = selectedEmployee?.user_uuid || selectedEmployee?.id;
    if (!userIdentifier) {
      console.error('No user identifier available');
      if (!skipEditingToggle) setIsEditing(false);
      setIsSaving(false);
      return;
    }

    // Process all pending deletions first
    for (const educationId of pendingDeletions) {
      try {
        const isNewRecord = !/^\d+$/.test(educationId);
        const numericId = isNewRecord ? 0 : parseInt(educationId);

        if (!isNewRecord && numericId > 0) {
          await educationService.deleteEducation(userIdentifier, numericId);
        }
        await removeEducation(educationId);
      } catch (error) {
        console.error(`Failed to delete education ${educationId}:`, error);
      }
    }

    // Process all pending changes
    for (const [educationId, changes] of Object.entries(pendingChanges)) {
      if (pendingDeletions.includes(educationId)) continue;
      if (Object.keys(changes).length === 0) continue;

      try {
        const educationEntry = selectedEmployee?.educations?.find(edu => edu.id === educationId);
        if (!educationEntry) continue;

        const updateData = {
          university: changes.university ?? educationEntry.university ?? '',
          education: changes.education ?? educationEntry.education ?? '',
          degree: changes.degree ?? educationEntry.degree ?? '',
          year: parseInt(changes.year ?? educationEntry.year ?? '0') || new Date().getFullYear()
        };

        const isNewRecord = !/^\d+$/.test(educationId);
        const numericId = isNewRecord ? 0 : parseInt(educationId);

        if (!isNewRecord && numericId > 0) {
          await educationService.updateEducation(userIdentifier, numericId, updateData);
        } else {
          const createResponse = await educationService.createEducation(userIdentifier, updateData);
          const newEducation = createResponse.education;
          if (newEducation && newEducation.id) {
            const realId = String(newEducation.id);
            await removeEducation(educationId);
            const storeState = useEmployeeProfileStore.getState();
            const currentEmployee = storeState.selectedEmployee;
            if (currentEmployee) {
              const newEducationEntry = {
                id: realId,
                university: changes.university || educationEntry.university || null,
                education: changes.education || educationEntry.education || null,
                degree: changes.degree || educationEntry.degree || null,
                year: changes.year || educationEntry.year || null,
              };
              const updatedEducations = [...(currentEmployee.educations || []), newEducationEntry];
              useEmployeeProfileStore.setState({
                selectedEmployee: {
                  ...currentEmployee,
                  educations: updatedEducations,
                  lastUpdated: new Date()
                }
              });
            }
            continue;
          }
        }

        await updateEducation(educationId, {
          university: changes.university || null,
          education: changes.education || null,
          degree: changes.degree || null,
          year: changes.year || null
        });
      } catch (error) {
        console.error(`Failed to save education ${educationId}:`, error);
      }
    }

    setPendingChanges({});
    setPendingDeletions([]);
    if (!skipEditingToggle) { setIsEditing(false); onEditingChange?.(false); }
    setIsSaving(false);
  }, [isSaving, selectedEmployee, pendingDeletions, pendingChanges, removeEducation, updateEducation, setIsEditing, onEditingChange]);

  const prevExternalIsEditing = useRef(externalIsEditing);
  useEffect(() => {
    if (!externalIsEditing && prevExternalIsEditing.current) {
      handleSave(true);
    }
    prevExternalIsEditing.current = externalIsEditing;
  }, [externalIsEditing, handleSave]);

  const Separator = () => (
    <div className="w-full h-px bg-slate-200" />
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Education</h3>
        <div className="flex items-center gap-2">
          {isEditing && (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleAddEducation}
            >
              <Plus className="w-4 h-4" />
              Add Education
            </Button>
          )}
          {!readOnly && externalIsEditing === undefined && (
            <>
              {!isEditing ? (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  disabled={disableEdit}
                  className={disableEdit ? 'opacity-40 cursor-not-allowed' : ''}
                  onClick={() => { setIsEditing(true); onEditingChange?.(true); }}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => handleSave()}
                    disabled={isSaving}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => {
                      setPendingChanges({});
                      setPendingDeletions([]);
                      setIsEditing(false);
                      onEditingChange?.(false);
                    }}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {visibleEducationEntries.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-4">No education records found.</div>
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>University</TableHead>
                <TableHead>Education</TableHead>
                <TableHead>Degree</TableHead>
                <TableHead className="w-[120px]">Year</TableHead>
                {isEditing && <TableHead className="w-[60px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleEducationEntries.map((entry) => {
                const pending = pendingChanges[entry.id] || {};
                return (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={pending.university ?? entry.university ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateEducation(entry.id, 'university', e.target.value)}
                          placeholder="Enter university"
                        />
                      ) : (
                        <span className="text-sm text-slate-900">{entry.university || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={pending.education ?? entry.education ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateEducation(entry.id, 'education', e.target.value)}
                          placeholder="Enter education"
                        />
                      ) : (
                        <span className="text-sm text-slate-900">{entry.education || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={pending.degree ?? entry.degree ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateEducation(entry.id, 'degree', e.target.value)}
                          placeholder="Enter degree"
                        />
                      ) : (
                        <span className="text-sm text-slate-900">{entry.degree || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={pending.year ?? entry.year ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const numericValue = e.target.value.replace(/\D/g, '').slice(0, 4);
                            handleUpdateEducation(entry.id, 'year', numericValue);
                          }}
                          placeholder="YYYY"
                          inputMode="numeric"
                          maxLength={4}
                        />
                      ) : (
                        <span className="text-sm text-slate-900">{entry.year || '-'}</span>
                      )}
                    </TableCell>
                    {isEditing && (
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => handleRemoveEducation(entry.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Education"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default EducationProfileSection;
